import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { box, text, grid, boxCustom, line, textWrap, labelValue, addAnuladoWatermark } from "../pdfDraw";
import { formatDate, formatDecimal, formatText, formatMoney, formatMoneySpecial } from "../../formatters";
import exampleLogo from "../../../assets/img/react.jpg";
import QRCode from "qrcode";
import { encryptText } from '../../../controllers/api';
import { recordPrintout } from "../../../controllers/api_invoices";

const addWatermark = (doc, text = "DOCUMENTO SIN DERECHO A CRÉDITO FISCAL") => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  //console.log('addWatermark-text: ', text);
  doc.saveGraphicsState();

  // Transparencia (requiere jsPDF >= 2.5)
  doc.setGState(new doc.GState({ opacity: 0.1 }));

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(150, 150, 150);

  // Centrar + rotar
  doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 46.57, {
    align: "center",
    angle: 0
  });

  doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 93.14, {
    align: "center",
    angle: 0
  });

  doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 139.7, {
    align: "center",
    angle: 0
  });

  doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 186.28, {
    align: "center",
    angle: 0
  });

  doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 232.85, {
    align: "center",
    angle: 0
  });

  doc.restoreGraphicsState();
};

// (2026-07-08) addAnuladoWatermark se movió a ../pdfDraw (compartida por
// factura, retención y guía). Se importa arriba.

export const buildInvoicesPDF = async (data, invoiceId, mode = "download") => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
  /*if (data.documento.es_copia == true){
    const pageCount = doc.getNumberOfPages();
    console.log('pageCount: ', pageCount);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addWatermark(doc); //"BORRADOR"
    }
  }*/
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 8;

  const { documento, emisor, tercero, receptor: cliente, items, totales, pagos_detalle } = data;

  // ── (2026-07-22 Viajes) Nodo `/viajes` — agencias de viaje / boletería aérea ──
  // Lista de boletos del documento (1:N). Lectura DEFENSIVA y estrictamente
  // condicional: si el documento no trae boletos (el caso de todas las empresas
  // que no son agencia de viajes) `viajes` queda vacío y el PDF se dibuja
  // EXACTAMENTE igual que hoy — ni bloque, ni cambio de alturas.
  const viajes = Array.isArray(data.viajes)
    ? data.viajes
    : (Array.isArray(documento?.viajes) ? documento.viajes : []);
  // Altura reservada para el bloque de viajes (título + cabecera + N filas de
  // dos líneas). Se DESCUENTA del relleno de filas vacías de la tabla de ítems
  // para no desplazar el pie de página legal.
  const viajesBlockH = viajes.length > 0 ? (10 + viajes.length * 10) : 0;

  const layout = grid(12, margin, pageWidth, 2);

  let y = 10;

  /* =====================================================
     HEADER SUPERIOR
  ===================================================== */

  const colEmpresa = layout.col(0, 7);
  const colFactura = layout.col(7, 5);
  let padding = 3; // espacio interno
  /* ===== EMPRESA ===== */
  let empresaY = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(formatText(emisor.nombre_empresa || ""), colEmpresa.x, empresaY);

  empresaY += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(formatText(emisor.rif || ""), colEmpresa.x, empresaY);

  empresaY += 2;

  const colLogo = layout.col(0, 3);
  const colInfoEmpresa = layout.col(3, 4);
  /* ===== LOGO ===== */
  try {
    doc.addImage(
      emisor?.logo_base64 || exampleLogo,
      emisor?.logo_base64 ? "PNG" : "JPG",
      colLogo.x,
      empresaY,
      colLogo.w - 5,
      (emisor.direccion_fiscal != '' ? 30 : 25),
    );
  } catch {
    doc.addImage(exampleLogo, "JPG", colLogo.x, y, colLogo.w, 25);
  }

  /* ==== Informacion de la Empresa ==== */
  doc.setFontSize(7);
  empresaY += 6;
  /* ==== BLOQUE INFO EMPRESA EN CAJA ==== */
  // Calcular altura total del boxCustom
  let tempY = empresaY;
  tempY = textWrap(
    doc,
    formatText(emisor.direccion_fiscal) || "",
    0, // X no importa para medir
    tempY,
    colInfoEmpresa.w - padding * 2,
    7,
    "justify",
    false,
    3,
    true // measureOnly
  );

  tempY = textWrap(
    doc,
    formatText(emisor.telefono) || "",
    0,
    tempY,
    colInfoEmpresa.w - padding * 2,
    7,
    "left",
    false,
    3,
    true
  );

  tempY = textWrap(
    doc,
    `Actividad Económica: ${emisor.actividad_economica ? (emisor.actividad_economica.codigo + " – " + emisor.actividad_economica.descripcion) : ""}`,
    0,
    tempY,
    colInfoEmpresa.w - padding * 2,
    7,
    "left",
    false,
    3,
    true
  );

  const boxHeight = tempY - empresaY + padding * 2;

  // Dibujar la caja con la altura exacta
  boxCustom(doc, colInfoEmpresa.x, empresaY, colInfoEmpresa.w, boxHeight, "#ecf6ff", 3, 0, "#ecf6ff");

  // Escribir el contenido dentro, ahora en orden lineal
  let currentInfoY = empresaY + padding;
  currentInfoY = textWrap(
    doc,
    formatText(emisor.direccion_fiscal) || "",
    colInfoEmpresa.x + padding,
    currentInfoY + padding,
    colInfoEmpresa.w - padding * 2,
    7,
    "justify",
    false,
    3
  );

  currentInfoY = textWrap(
    doc,
    formatText(emisor.telefono) || "",
    colInfoEmpresa.x + padding,
    currentInfoY,
    colInfoEmpresa.w - padding * 2,
    7,
    "left",
    false,
    3
  );

  currentInfoY = textWrap(
    doc,
    `Actividad Económica: ${emisor.actividad_economica ? (emisor.actividad_economica.codigo + " – " + emisor.actividad_economica.descripcion) : ""}`,
    colInfoEmpresa.x + padding,
    currentInfoY,
    colInfoEmpresa.w - padding * 2,
    7,
    "left",
    false,
    3
  );

  /* ===== CAJA NRO. DE CONTROL SENIAT (box independiente, arriba de FACTURA) =====
     (2026-05-18) Referencia estilo forma libre preimpresa: el N° de control
     asignado/SENIAT en su propia caja, separada de la caja FACTURA.
     (#117 Increment 1b) El valor mostrado es `documento.numero_control` — el
     ex-`serial`, Nº de Control SENIAT del lote (formato `XX-NNNNNNNN`,
     Providencia Adm. SNAT/2024/000102: identificador de hasta 3 dígitos +
     secuencial de 8 dígitos, iniciando '00-00000001'). El formato se configura
     en info_seniat.numero_control_mascara. El numero_control legacy per-empresa
     fue ELIMINADO; la caja inferior FACTURA queda SOLO con la etiqueta. */
  const _seniatTop = y - 6;
  const _seniatH = 8;
  boxCustom(doc, colFactura.x, _seniatTop, colFactura.w + 2, _seniatH, "#ecf6ff", 3, 0, "#ecf6ff");
  {
    const _lX = colFactura.x + padding;
    const _rX = colFactura.x + colFactura.w - padding;
    doc.setFont("helvetica", "bold");
    // Etiqueta — color por defecto (negro), bold.
    text(doc, "Nro. de control SENIAT:", _lX, _seniatTop + 5.4, 8, "left", true);
    // (#117 Increment 1b) Valor: documento.numero_control (ex-`serial`) — Nº de
    // Control SENIAT con formato XX-NNNNNNNN (lote del ente regulador, Providencia
    // SNAT/2024/000102). Maxima visibilidad (bold + rojo) por compliance.
    doc.setTextColor(220, 38, 38); // tailwind red-600
    text(doc, formatText(documento.numero_control || ""), _rX, _seniatTop + 5.4, 8, "right", true);
    doc.setTextColor(0, 0, 0); // reset a negro para no contaminar texto posterior
  }

  /* ===== CAJA FACTURA (desplazada debajo de la caja SENIAT) ===== */

  // (2026-05-18) Caja FACTURA más compacta y título con la MISMA fuente
  // (8) que "Nro. de control SENIAT", para que en NC/ND la cabecera no
  // exceda el espacio disponible.
  const _facturaTop = _seniatTop + _seniatH + 2;
  boxCustom(
    doc,
    colFactura.x,
    _facturaTop,
    colFactura.w + 2,
    7,
    "#ecf6ff",
    3,
    0,
    "#ecf6ff"
  );

  let leftX = colFactura.x + padding;
  let rightX = colFactura.x + colFactura.w - padding;

  let currentY = _facturaTop + 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);

  // Primera línea: "FACTURA"/"NOTA DE..." a la izquierda, número a la derecha
  text(doc, (documento.tipo_documento == "FC" ? "FACTURA" : (documento.tipo_documento == "NC" ? "NOTA DE CRÉDITO" : "NOTA DE DÉBITO")), leftX, currentY, 8, "left", true);
  text(doc, `${documento.numero_documento || documento.numero_factura || ""}`, rightX, currentY, 8, "right", true);

  currentY += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);

  // Segunda línea: fecha a la izquierda, hora a la derecha
  labelValue(doc, "Fecha emisión", formatDate(documento.fecha_emision) || "", leftX, currentY, 8, "left");
  //text(doc, `Fecha emisión: ${formatDate(documento.fecha_emision)}`, leftX, currentY, 8, "left", false);
  labelValue(doc, "Hora emisión", formatText(documento.hora_emision) || "", rightX - 32, currentY, 8, "right");
  //text(doc, `Hora emisión: ${formatText(documento.hora_emision)}`, pageWidth - margin, currentY, 8, "right", false);

  currentY += 6;
  /* =====================================================
     BLOQUE CONTROL
  ===================================================== */

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);

  // (#117 Increment 1b) Caja FACTURA inferior: SOLO la etiqueta (sin valor) —
  // decisión cerrada del cliente. El Nº de Control SENIAT (ex-`serial`) ya se
  // muestra arriba en bold rojo; el numero_control legacy per-empresa fue eliminado.
  text(doc, "N° de Control:", leftX + 20, currentY, 7, "left", true);

  currentY += 3;
  text(doc, "Fecha de asignación:", leftX + 20, currentY, 7, "left", true);
  text(doc, formatDate(documento.fecha_asignacion) || "", rightX + 3, currentY, 7, "right", false);

  // (2026-07-08 Grupo B) Campos SENIAT opcionales — se imprimen SOLO si vienen:
  // serie / cod_operacion desde la empresa (emisor); fecha_vencimiento del doc.
  if (emisor.serie) {
    currentY += 3;
    text(doc, "Serie:", leftX + 20, currentY, 7, "left", true);
    text(doc, formatText(emisor.serie), rightX + 3, currentY, 7, "right", false);
  }
  if (emisor.cod_operacion) {
    currentY += 3;
    text(doc, "Cód. Operación:", leftX + 20, currentY, 7, "left", true);
    text(doc, formatText(emisor.cod_operacion), rightX + 3, currentY, 7, "right", false);
  }
  if (documento.fecha_vencimiento) {
    currentY += 3;
    text(doc, "Fecha de Vencimiento:", leftX + 20, currentY, 7, "left", true);
    text(doc, formatDate(documento.fecha_vencimiento) || "", rightX + 3, currentY, 7, "right", false);
  }

  if (documento.tipo_documento != 'FC') {
    currentY += 3;
    text(doc, "N° de factura afectada:", leftX + 20, currentY, 7, "left", true);
    text(doc, formatText(documento.factura_afectada_numero || ""), rightX + 3, currentY, 7, "right", false);

    // (2026-05-18) Unificado: las 3 líneas (fecha/hora emisión + fecha
    // asignación de la factura afectada) en una sola para que la cabecera
    // de NC/ND no exceda el espacio disponible.
    currentY += 3;
    text(doc, "Fecha de la factura:", leftX + 20, currentY, 7, "left", true);
    text(
      doc,
      (formatDate(documento.factura_afectada_fecha_emision || "") + " " + formatText(documento.factura_afectada_hora_emision || "")).trim(),
      rightX + 3, currentY, 7, "right", false
    );

    // (2026-07-18 A10) Monto de la factura afectada. Se imprime SOLO si el
    // backend lo entrega (factura_afectada_monto no vacío); se omite si no hay dato.
    if (documento.factura_afectada_monto) {
      currentY += 3;
      text(doc, "Monto factura afectada:", leftX + 20, currentY, 7, "left", true);
      text(doc, formatMoney(documento.factura_afectada_monto), rightX + 3, currentY, 7, "right", false);
    }
  }

  currentY += 3;
  text(doc, "Moneda:", leftX + 20, currentY, 7, "left", true);
  text(doc, formatText(data.moneda?.codigo || totales?.moneda || "VES"), rightX + 3, currentY, 7, "right", false);

  currentY += 3;
  text(doc, "Tasa de cambio:", leftX + 20, currentY, 7, "left", true);
  text(doc, formatMoneySpecial(totales.tipo_cambio_bcv, 4), rightX + 3, currentY, 7, "right", false);

  // (2026-07-08) Borde inferior de la COLUMNA más alta (info empresa vs caja
  // FACTURA, que ahora puede crecer con serie/cod_operacion/vencimiento) para no
  // solapar el separador ni el contenido de abajo.
  currentY = Math.max(currentInfoY, currentY) + 5;

  line(doc, margin, currentY, pageWidth - margin, currentY);
  const blocksStartY = currentY + 6;
  y = blocksStartY;
  let separatorY = 0;

  //y += 32;

  /* =====================================================
     BLOQUE TERCERO (Si aplica)
  ===================================================== */
  if (tercero) {
    // (2026-05-20) Header de sección "TERCERO" para identificar visualmente
    // el bloque cuando es una factura por cuenta de terceros.
    // El texto legal del Art. 10 IVA YA NO va aquí: se inyecta como primera
    // fila del cuerpo de items (ver BLOQUE TABLA DE ITEMS más abajo) para
    // cumplir el requerimiento del cliente — aparece en la descripción de
    // la factura antes del listado de productos.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("TERCERO (AFILIADA)", margin + 5, y);
    y += 3.5;

    doc.setFontSize(8);
    doc.text("Nombre / Razón Social", margin + 5, y);
    doc.text("Correo", pageWidth / 4, y);
    doc.text("Teléfono", pageWidth / 2, y);

    y += 3;

    doc.setFont("helvetica", "normal");
    textWrap(
      doc,
      formatText(tercero.nombre_empresa || tercero.denominacion_comercial || ""),
      margin + 5,
      y,
      (pageWidth / 4) - 15,
      7,
      "justify",
      false,
      3
    );
    doc.text(tercero.email || "", pageWidth / 4, y);
    doc.text(tercero.telefono || "0000-0000000", pageWidth / 2, y);

    y += 5;

    doc.setFont("helvetica", "bold");
    doc.text("RIF", margin + 5, y);
    doc.text("Dirección", pageWidth / 4, y);

    y += 3;

    doc.setFont("helvetica", "normal");
    doc.text(tercero.rif || "", margin + 5, y);

    const addressTerceroY = textWrap(
      doc,
      formatText(tercero.direccion_fiscal || ""),
      (pageWidth / 4),
      y,
      (pageWidth / 2) - 20,
      7,
      "justify",
      false,
      3
    );

    y += 4 + (addressTerceroY - y);
    separatorY = y;
    line(doc, margin, separatorY, pageWidth - margin, separatorY);
    y += 4;
    doc.setFontSize(7);
  }

  /* =====================================================
     BLOQUE CLIENTE
  ===================================================== */

  // (2026-05-20) Label en mayúsculas para identificación clara del bloque
  // (en facturas con tercero, evita confusión emisor/tercero/cliente).
  doc.setFont("helvetica", "bold");
  doc.text("CLIENTE", margin + 5, y);
  doc.text("Correo", pageWidth / 4, y);
  doc.text("Teléfono", pageWidth / 2, y);
  doc.setFont("helvetica", "normal");
  //doc.text(cliente.nombre || "", margin + 25, y);

  y += 3;

  textWrap(
    doc,
    formatText(cliente.nombre || ""),
    margin + 5,
    y,
    (pageWidth / 4) - 15,
    7,
    "justify",
    false,
    3
  );
  doc.text(cliente.correo || "", pageWidth / 4, y);
  doc.text(cliente.telefono || "0000-0000000", pageWidth / 2, y);

  y += tercero ? 5 : 9;

  doc.setFont("helvetica", "bold");
  doc.text("Documento", margin + 5, y);
  doc.text("Dirección", pageWidth / 4, y);

  y += 3;

  doc.setFont("helvetica", "normal");
  doc.text(`RIF: ${cliente.rif || ""}`, margin + 5, y);

  const addressY = textWrap(
    doc,
    formatText(cliente.direccion || ""),
    (pageWidth / 4),
    y,
    (pageWidth / 2) - 20,
    7,
    "justify",
    false,
    3
  );

  const addressLenY = textWrap(
    doc,
    formatText(cliente.direccion || ""),
    (pageWidth / 4),
    0,
    (pageWidth / 2) - 20,
    7,
    "justify",
    false,
    3,
    true
  );

  /* ---------- QR ---------- */

  const baseUrl = window.location.origin;

  const qrUrl = `${baseUrl}/document/INV/${encodeURIComponent(encryptText(documento.qr_token.toString()))}/`;

  const qrBase64 = await QRCode.toDataURL(qrUrl, {
    width: 120,
    margin: 1
  });
  const qrSize = 22; // Tamaño fijo para todos los casos, evita deformaciones si la dirección es muy larga

  // Calcular la posición Y centrada
  const topSeparatorY = blocksStartY - 6; // La línea superior se dibuja 6 puntos antes de blocksStartY
  const bottomY = tercero ? separatorY : addressY; // En ventas normales, el límite inferior es el fin de la dirección
  const availableHeight = bottomY - topSeparatorY;

  // Si es normal, bajamos el QR un poco (1 punto) para que no pegue con la línea superior, sin solapar la tabla
  const qrY = topSeparatorY + (availableHeight - qrSize) / 2 + (tercero ? 0 : 1);

  /*
   * @param {object} doc - Instancia de jsPDF
   * @param {number} x - Posición X inicial
   * @param {number} y - Posición Y inicial
   * @param {number} w - Ancho de la caja
   * @param {number} h - Alto de la caja
   * @param {string|array} fillColor - Color de fondo (hex o RGB) (default "#FFFFFF")
   * @param {number} borderRadius - Radio de los bordes (default 0)
   * @param {number} borderWidth - Grosor del borde en mm (default 0, sin borde)
   * @param {string|array} borderColor - Color del borde (hex o RGB) (default "#000000")
  */
  boxCustom(doc, (pageWidth - margin - qrSize) - 6, qrY - 1, qrSize + 2, qrSize + 2, "#ffffff", 2, 0.5, "#4a4a4a");
  doc.addImage(
    qrBase64,
    "PNG",
    (pageWidth - margin - qrSize) - 5,
    qrY,
    qrSize,
    qrSize
  );

  y = addressY + 3;

  /* =====================================================
     TABLA
  ===================================================== */
  // `data.moneda` = objeto moneda raíz del servicio (codigo, simbolo, posicion).
  // `totales.moneda` = código string fallback.
  const monedaCodigo = (data.moneda?.codigo || totales?.moneda || "VES").toUpperCase();
  let isDual = monedaCodigo !== "VES";
  // sym: símbolo de la moneda aplicada
  let sym = data.moneda?.simbolo || ({ USD: "$", EUR: "€", VES: "Bs." }[monedaCodigo] || monedaCodigo);
  // sym_bs: símbolo de Bolívares (siempre Bs.)
  const sym_bs = "Bs.";

  const marginTb = 8;
  // (2026-07-22 Viajes) `viajesBlockH` es 0 cuando no hay boletos => el alto
  // disponible queda IDÉNTICO al de hoy para cualquier documento sin viajes.
  const maxTableHeight = (tercero ? 85 : 120) - viajesBlockH; // Restaurar 120 para normales, 85 para terceros
  const rowHeight = 7; // depende del tamaño de fuente y padding (aprox)

  // (2026-05-22) La descripción del item viene resuelta SERVER-SIDE:
  //   - producto.es_tercero=true  => descripcion combinada multilínea
  //     (providencia_terceros + RIF/nombre tercero + nombre producto)
  //   - producto.es_tercero=false => nombre del producto (producto.nombre)
  // El código (columna Cod.) y demás datos del producto provienen del
  // objeto `i.producto`; `i.descripcion` ya viene resuelto server-side
  // y sus `\n` se honran generando filas multilínea.
  const currentTableHeight = items.length * rowHeight;
  const remainingHeight = maxTableHeight - currentTableHeight;
  const emptyRowsCount = Math.max(0, Math.ceil(remainingHeight / rowHeight));
  // (2026-07-20 Recargos) 8 columnas ahora (se agregó "Recargo" tras "Monto Desc.").
  const emptyRows = Array.from({ length: emptyRowsCount }, () => ["", "", "", "", "", "", "", ""]);
  const tableBody = [
    ...items.map(i => [
      formatText(i.producto?.codigo || ""),
      formatText(i.descripcion || ""),
      formatDecimal(i.cantidad),
      formatDecimal(isDual ? (i.precio_unitario_aplicada || i.precio_unitario) : i.precio_unitario),
      formatDecimal(i.tasa_iva) + "%",
      formatDecimal(isDual ? (i.descuento_aplicada || i.monto_descuento) : i.monto_descuento),
      // (2026-07-20 Recargos) recargo de línea (monto). Espejo del descuento.
      formatDecimal(isDual ? (i.recargo_monto_aplicada || i.recargo_monto) : i.recargo_monto),
      formatDecimal(isDual ? (i.total_aplicada || i.total) : i.total),
    ]),
    ...emptyRows,
  ];
  autoTable(doc, {
    startY: y,
    margin: { left: marginTb, right: marginTb },
    tableWidth: 'auto',
    head: [["Cod.", "Descripción", "Cantidad", "P. Unit.", "% IVA", "Monto Desc.", "Recargo", "Total"]],
    body: tableBody,
    styles: {
      fontSize: 7,
      //cellPadding: 2,
      lineColor: [100, 119, 140],
    },
    headStyles: {
      fillColor: [235, 246, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineWidth: 0 // quitamos bordes laterales
    },
    didParseCell: function (data) {
      /* ===== HEADER ===== */
      if (data.section === "head") {
        // Solo dibujar borde superior e inferior
        data.cell.styles.lineWidth = {
          top: 0.6,
          bottom: 0.6,
          left: 0,
          right: 0
        };
        data.cell.styles.halign = "center";
        data.cell.styles.lineColor = [100, 119, 140];
      }
      /* ===== BODY ===== */
      if (data.section === "body") {
        data.cell.styles.lineWidth = {
          top: 0,
          bottom: 0,
          left: 0.1,
          right: 0.1
        };
        data.cell.styles.lineColor = [100, 119, 140];
      }
    },
    bodyStyles: {
      textColor: [60, 60, 60]
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]    // efecto zebra opcional
    },
    columnStyles: {
      // (2026-07-20 Recargos) se insertó la columna "Recargo" en el índice 6;
      // "Total" pasó al índice 7. Se reduce ligeramente Descripción para dar cabida.
      0: { halign: "center", cellWidth: 18 },
      1: { cellWidth: 62 },
      2: { halign: "right", cellWidth: 18 },
      3: { halign: "right", cellWidth: 22 },
      4: { halign: "center", cellWidth: 15 },
      5: { halign: "right", cellWidth: 22 },
      6: { halign: "right", cellWidth: 22 },
      7: { halign: "right", cellWidth: 25 }
    },
    didDrawPage: function (data) {
      if (documento.es_copia == true) {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        doc.saveGraphicsState();

        try {
          doc.setGState(new doc.GState({ opacity: 0.08 }));
        } catch (e) {
          // fallback por si GState no aplica
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(180, 180, 180);
        //console.log('pageHeight: ', pageHeight);
        doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 46.57, {
          align: "center",
          angle: 0
        });

        doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 93.14, {
          align: "center",
          angle: 0
        });

        doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 139.7, {
          align: "center",
          angle: 0
        });

        doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 186.28, {
          align: "center",
          angle: 0
        });

        doc.text("DOCUMENTO SIN DERECHO A CRÉDITO FISCAL", pageWidth / 2, 232.85, {
          align: "center",
          angle: 0
        });

        doc.restoreGraphicsState();
      }
    }
  });

  y = doc.lastAutoTable.finalY;

  line(doc, margin, y, pageWidth - margin - 2, y);

  y = doc.lastAutoTable.finalY + 5;

  /* =====================================================
     (2026-07-22 Viajes) DATOS DEL VIAJE (BOLETERÍA AÉREA)
     Nodo `/viajes` de la norma: 15 datos de pasajero y boleto, agrupados en 8
     columnas para que quepan a lo ancho de la hoja sin perder ningún dato
     (cada celda puede llevar dos líneas). Una fila por boleto (1:N).
     Bloque ESTRICTAMENTE condicional: sin boletos no se dibuja nada y el PDF
     queda idéntico al actual.
  ===================================================== */
  if (viajes.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("DATOS DEL VIAJE (BOLETERÍA AÉREA)", margin, y);
    doc.setFont("helvetica", "normal");

    const _ln = (a, b) => [formatText(a || ""), formatText(b || "")]
      .filter((t) => t !== "")
      .join("\n");

    const tableBodyVj = viajes.map((v) => [
      // Pasajero: nombre + "TIPO-NUMERO" de identificación.
      _ln(v.nombre_apellido_pasajero,
          [v.tipo_identificacion, v.numero_identificacion].filter(Boolean).join("-")),
      // Domicilio fiscal + teléfono del pasajero.
      _ln(v.domicilio_fiscal_pasajero, v.numero_tele_pasajero),
      formatText(v.razon_social_serv_transporte || ""),
      formatText(v.numero_boleto || ""),
      // Salida: fecha + hora / punto de salida.
      _ln([v.fecha_salida, v.hora_salida].filter(Boolean).join(" "), v.punto_salida),
      // Llegada: fecha + hora / punto de destino.
      _ln([v.fecha_llegada, v.hora_llegada].filter(Boolean).join(" "), v.punto_destino),
      formatText(v.nombre_puerto_embarque || ""),
      formatText(v.condiciones_entrega || ""),
    ]);

    autoTable(doc, {
      startY: y + 1.5,
      margin: { left: margin, right: margin },
      tableWidth: 'auto',
      head: [[
        "Pasajero / Ident.",
        "Domicilio / Teléfono",
        "Transportista",
        "Boleto",
        "Salida",
        "Llegada",
        "Puerto embarque",
        "Cond. entrega",
      ]],
      body: tableBodyVj,
      styles: {
        fontSize: 6,
        cellPadding: 0.7,
        lineColor: [100, 119, 140],
      },
      headStyles: {
        fillColor: [235, 246, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0,
      },
      didParseCell: function (dataCell) {
        if (dataCell.section === "head") {
          dataCell.cell.styles.lineWidth = { top: 0.6, bottom: 0.6, left: 0, right: 0 };
          dataCell.cell.styles.halign = "center";
          dataCell.cell.styles.lineColor = [100, 119, 140];
        }
        if (dataCell.section === "body") {
          dataCell.cell.styles.lineWidth = { top: 0, bottom: 0.1, left: 0.1, right: 0.1 };
          dataCell.cell.styles.lineColor = [100, 119, 140];
        }
      },
      bodyStyles: { textColor: [60, 60, 60] },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 30 },
        2: { cellWidth: 26 },
        3: { halign: "center", cellWidth: 20 },
        4: { cellWidth: 24 },
        5: { cellWidth: 24 },
        6: { cellWidth: 24 },
        7: { cellWidth: 20 },
      },
    });

    y = doc.lastAutoTable.finalY + 5;
  }

  /* =====================================================
     LAYOUT INFERIOR: 3 columnas de 30%, separadas por 5%
     usable = pageWidth - 2*margin = 200mm
     col = 60mm  |  gap = 10mm
     Col1: x=margin(8)  a x=68
     Col2: x=78         a x=138
     Col3: x=148        a x=208 (=pageWidth-margin)
  ===================================================== */
  const usable = pageWidth - 2 * margin;  // 200mm
  const colW = usable * 0.30;            // 60mm
  const gapW = usable * 0.05;            // 10mm
  const col1X = margin;                   //  8mm
  const col2X = col1X + colW + gapW;      // 78mm
  const col3X = col2X + colW + gapW;      // 148mm
  const col1Right = col1X + colW;             // 68mm
  const col2Right = col2X + colW;             // 138mm
  const col3Right = col3X + colW;             // 208mm

  /* =====================================================
     PAGOS (Col 1) — confinado a col1X … col1Right
  ===================================================== */
  if (pagos_detalle.length > 0) {
    const tableBodyMp = [...pagos_detalle.map(p => [
      formatText(p.metodo_nombre),
      formatText((p.aplica_igtf == false && p.banco != "") ? p.banco : ''),
      formatMoney(p.monto, p.moneda || null),
    ]),];
    autoTable(doc, {
      startY: y - 2,
      margin: { left: col1X, right: pageWidth - col1Right },
      tableWidth: colW,
      head: [["Medio de pago", "Banco", "Monto"]],
      body: tableBodyMp,
      styles: {
        fontSize: 6.5,
        cellPadding: 0.7,
        lineColor: [100, 119, 140],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0,
      },
      didParseCell: function (data) {
        if (data.section === "head") {
          data.cell.styles.lineWidth = { top: 0.0, bottom: 0.6, left: 0, right: 0 };
          data.cell.styles.halign = "center";
          data.cell.styles.lineColor = [100, 119, 140];
        }
        if (data.section === "body") {
          data.cell.styles.lineWidth = { top: 0.1, bottom: 0.1, left: 0.0, right: 0.0 };
          data.cell.styles.lineColor = [100, 119, 140];
        }
      },
      bodyStyles: { textColor: [60, 60, 60] },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      columnStyles: {
        0: { halign: "left", cellWidth: colW * 0.36 },
        1: { halign: "center", cellWidth: colW * 0.33 },
        2: { halign: "right", cellWidth: colW * 0.31 },
      },
    });

    // Observación debajo de pagos, dentro de col 1
    const yObsStart = doc.lastAutoTable.finalY + 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("Observación:", col1X, yObsStart);
    doc.setFont("helvetica", "normal");
    textWrap(
      doc,
      formatText(documento.observacion || ""),
      col1X,
      yObsStart + 4,
      colW,
      7,
      "justify",
      false,
      3
    );
  }

  /* =====================================================
     TOTALES — Col 2 (Bs.) y Col 3 (Aplicada/$)
  ===================================================== */
  doc.setFontSize(7);

  // Columna Bs. (col2) y Aplicada (col3).
  const totalsX_BS = col2X;
  const rightX_BS = col2Right;
  const totalsX_AP = col3X;
  const rightX_AP = col3Right;

  // (2026-05-18) Facturas en VES (NO dual): se muestra UNA sola sección de
  // totales = C2 (moneda del documento), alineada a la DERECHA (col3), con
  // símbolo "Bs." y la base de IGTF ya convertida
  // (monto_pagado_divisas_aplicado). En multimoneda (isDual) se mantienen
  // AMBAS columnas: C1 "Bs." (col2) + C2 aplicada (col3) — sin cambios.
  // mkLabel(sym, isC1): el flag isC1 solo afecta la base del IGTF.
  const drawTot = (mkLabel, c1Val, c2Val) => {
    if (isDual) {
      doc.text(mkLabel(sym_bs, true), totalsX_BS, y);
      doc.text(formatDecimal(c1Val), rightX_BS, y, { align: "right" });
      doc.text(mkLabel(sym, false), totalsX_AP, y);
      doc.text(formatDecimal(c2Val), rightX_AP, y, { align: "right" });
    } else {
      doc.text(mkLabel(sym_bs, false), totalsX_AP, y);
      doc.text(formatDecimal(c2Val), rightX_AP, y, { align: "right" });
    }
  };

  if (Number(totales.monto_exento) > 0) {
    drawTot(
      (s) => "Exento " + s + ":",
      totales.monto_exento,
      totales.monto_exento_aplicada || 0
    );
    y += 3;
  }

  doc.setFont("helvetica", "normal");
  const lineHeight = 3;
  for (let i = 0; i < totales.desglose_impuestos.length; i++) {
    const tasa = formatDecimal(totales.desglose_impuestos[i].tasa);
    drawTot(
      (s) => "Base imp. IVA " + tasa + "% " + s + ":",
      totales.desglose_impuestos[i].base,
      totales.desglose_impuestos[i].base_aplicada || 0
    );
    y += lineHeight;

    drawTot(
      (s) => "IVA " + tasa + "% " + s + ":",
      totales.desglose_impuestos[i].iva,
      totales.desglose_impuestos[i].iva_aplicada || 0
    );
    y += lineHeight;
  }

  // (2026-07-08 Grupo A) Descuento Total (Bs y $). Solo si hay descuento (>0);
  // la base imponible de arriba ya va neta del descuento.
  if (Number(totales.total_descuento) > 0 || Number(totales.total_descuento_aplicada) > 0) {
    drawTot(
      (s) => "Descuento Total " + s + ":",
      totales.total_descuento,
      totales.total_descuento_aplicada || 0
    );
    y += lineHeight;
  }

  // (2026-07-20 Recargos) Recargo Total (Bs y $). Solo si hay recargo (>0); la
  // base imponible de arriba ya va prorrateada con el recargo global. Espejo del
  // Descuento Total.
  if (Number(totales.total_recargos) > 0 || Number(totales.total_recargos_aplicada) > 0) {
    drawTot(
      (s) => "Recargo Total " + s + ":",
      totales.total_recargos,
      totales.total_recargos_aplicada || 0
    );
    y += lineHeight;
  }

  drawTot(
    (s) => "Total Factura " + s + ":",
    totales.total_factura,
    totales.total_factura_aplicada || 0
  );

  // (2026-05-18) La línea IGTF solo se muestra cuando hay un pago con IGTF
  // vinculado a la factura (es decir, monto IGTF > 0 en C1 o C2). Si no hay
  // pago con IGTF, se omite la línea y se deja un único espacio entre
  // "Total Factura" y "Total a Pagar".
  const _hayIgtf =
    (Number(totales.igtf_monto) || 0) > 0 ||
    (Number(totales.igtf_monto_aplicada) || 0) > 0;

  if (_hayIgtf) {
    y += 3;
    // IGTF: base en C1 = monto_pagado_divisas_calculado (suma de pagos con
    // IGTF convertidos a VES); base en C2 = monto_pagado_divisas_aplicado
    // (base YA convertida a la moneda del documento que persiste A1).
    drawTot(
      (s, isC1) => "IGTF " + formatDecimal(totales.igtf_porcentaje) + "% (" + s +
        formatDecimal(isC1
          ? totales.monto_pagado_divisas_calculado
          : (totales.monto_pagado_divisas_aplicado || 0)) + ") " + s + ":",
      totales.igtf_monto,
      totales.igtf_monto_aplicada || 0
    );
    y += 5;
  } else {
    y += 6;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);

  // (2026-05-23 v6 fix bug #3 IGTF doble) Usar total_a_pagar del backend
  // (nuevo campo desde v6) que ya viene calculado como total_factura + IGTF.
  // Fallback al cálculo manual SOLO si el backend es viejo y no expone el
  // campo, manteniendo retro-compatibilidad.
  //
  // En el backend v6: total_factura = base + IVA (sin IGTF), total_a_pagar
  // = base + IVA + IGTF. Sumar IGTF aquí ya NO duplica.
  //
  // En backend pre-v6: total_factura = base + IVA + IGTF (con IGTF dentro),
  // por lo que sumar IGTF aquí duplicaba. El fallback abajo mantiene el
  // comportamiento previo SOLO si total_a_pagar es null (backend viejo).
  const _tap = totales.total_a_pagar;
  const _tapAp = totales.total_a_pagar_aplicada;
  const totalAPagar = (_tap != null && _tap !== "")
    ? parseFloat(_tap)
    : parseFloat(totales.total_factura || 0) + parseFloat(totales.igtf_monto || 0);
  const totalAPagarAp = (_tapAp != null && _tapAp !== "")
    ? parseFloat(_tapAp)
    : parseFloat(totales.total_factura_aplicada || 0) + parseFloat(totales.igtf_monto_aplicada || 0);

  drawTot(
    (s) => "Total a Pagar " + s + ":",
    totalAPagar,
    totalAPagarAp
  );

  y += 12;

  /* ================================================= */
  /* ================= FOOTER ======================== */
  /* ================================================= */

  const footerHeight = 35;
  const footerTop = doc.internal.pageSize.height - footerHeight;

  /* =====================================================
     BLOQUE LEGAL INFERIOR
  ===================================================== */

  doc.setFontSize(6);
  doc.setFont("helvetica", "normal");

  const maxWidth = pageWidth - margin * 2;
  // (2026-07-07) Coletilla fiscal informativa (IGTF/RLIVA). Viene del lote vía
  // data.legal.coletilla_fiscal (editable por BD) y se imprime EN SU CASO
  // NATURAL — NO se pasa por formatText(), que la volvería MAYÚSCULAS. Si el
  // lote no la trae, cae al texto estándar de respaldo de abajo.
  y = textWrap(
    doc,
    ((typeof data.legal?.coletilla_fiscal === "string" && data.legal.coletilla_fiscal.trim()) ? data.legal.coletilla_fiscal.trim() : "") || "Este pago estará sujeto al cobro adicional del 3% del Impuesto a las Grandes Transacciones Financieras (IGTF), de conformidad con la Providencia Administrativa SNAT/2022/000013 publicada en la G.O. N° 42.339 del 17-03-2022, en caso de ser cancelado en divisas. No aplicara en pago en Bs. Este documento se expresa en Bolívares con su equivalencia en divisas al tipo de cambio corriente del mercado a la fecha de su emisión, según lo establecido en el artículo 13 numeral 14 de la Providencia Administrativa SNAT/2011/00071 (...) en concordancia con el artículo 128 de la Ley del Banco Central de Venezuela (BCV); artículo 25 de la Ley que establece el Impuesto al valor Agregado (IVA) y 38 del Reglamento General de la Ley que establece el Impuesto al Valor Agregado (RLIVA)",
    margin,       // X inicial
    footerTop,            // Y inicial
    maxWidth,     // ancho máximo
    5,            // tamaño fuente
    "center",     // alineación centrada
    true,          // negrita
    2.5
  );

  y += 3;
  // (2026-07-18 B1-bis) "DOCUMENTO EMITIDO CONFORME A LA PROVIDENCIA
  // ADMINISTRATIVA {X}." Se ELIMINA el fallback falso de nomenclatura de
  // providencia: la línea SOLO se imprime si la factura (snapshot del lote
  // info_seniat) trae la nomenclatura real; si falta, la línea se omite.
  const _docProvNom = formatText(data.legal.providencia_nomenclatura);
  if (_docProvNom) {
    y = textWrap(
      doc,
      `DOCUMENTO EMITIDO CONFORME A LA PROVIDENCIA ADMINISTRATIVA ${_docProvNom}.`,
      margin,       // X inicial
      y,            // Y inicial
      maxWidth,     // ancho máximo
      6,            // tamaño fuente
      "center",     // alineación centrada
      false,          // negrita
      3
    );
  }
  // (2026-07-18 B1-bis) Línea legal del pie = campo `leyenda_legal` del lote
  // info_seniat (llega como data.legal.leyenda_pie desde el backend). Se
  // imprime VERBATIM (SIN formatText/mayúsculas); si viene vacío, se omite.
  // ANTES esta posición componía "Imprenta Digital Autorizada mediante
  // Providencia Administrativa {nomenclatura} de fecha {fecha}"; el cliente
  // definió que el texto del pie proviene ÍNTEGRO de `leyenda_legal`. Además
  // se ELIMINÓ la impresión duplicada de `leyenda_pie` que existía unas líneas
  // arriba (misma fuente: factura.leyenda_legal) para no repetir el texto.
  const _leyendaLegal = (data.legal.leyenda_pie || "").toString().trim();
  if (_leyendaLegal) {
    y = textWrap(
      doc,
      _leyendaLegal,
      margin,       // X inicial
      y,            // Y inicial
      maxWidth,     // ancho máximo
      6,            // tamaño fuente
      "center",     // alineación centrada
      false,          // negrita
      3
    );
  }
  // (2026-07-18 B1-bis) Rango de Nº de Control del lote activo info_seniat:
  // referencia_inicial/final formateadas con la máscara de control por el
  // backend (data.legal.referencia_inicial / referencia_final). Se ELIMINA el
  // hardcode del rango de control inventado (números y fecha fijos). Sin datos
  // → línea omitida. El lote no tiene fecha de asignación de rango, por lo que
  // la fecha se omite.
  const _refIni = (data.legal.referencia_inicial || "").toString().trim();
  const _refFin = (data.legal.referencia_final || "").toString().trim();
  if (_refIni && _refFin) {
    y = textWrap(
      doc,
      `Nro. de Control Desde ${_refIni} Hasta Nro. ${_refFin}.`,
      margin,       // X inicial
      y,            // Y inicial
      maxWidth,     // ancho máximo
      6,            // tamaño fuente
      "center",     // alineación centrada
      false,          // negrita
      3
    );
  }
  // (2026-07-08) Marca "ANULADO" en TODAS las páginas si la factura está anulada.
  // Se aplica al final, sobre el contenido ya dibujado. `estatus` lo envía el
  // serializer (documento.estatus = 'normal' | 'anulada').
  if (documento.estatus === "anulada") {
    const _pc = doc.getNumberOfPages();
    for (let _p = 1; _p <= _pc; _p++) { doc.setPage(_p); addAnuladoWatermark(doc, documento.fecha_anulacion); }
  }

  const pdfBlob = doc.output("blob");
  if (mode === "download") {
    var baseName = 'FACT_';
    if (documento.tipo_documento == "NC") {
      baseName = 'NCRED_';
    }
    if (documento.tipo_documento == "ND") {
      baseName = 'NDEB_';
    }
    var response_recordPrintout = recordPrintout(invoiceId);
    doc.save(`${baseName + (documento.numero_documento || documento.numero_factura || "")}.pdf`);
  } else if (mode === "base64") {
    // (2026-05-21) Modo "base64": devuelve el PDF como string base64 puro
    // (sin el prefijo data: URL). Lo usa el flujo de notificacion automatica
    // por correo: el cliente genera el PDF, lo envia al backend en el body
    // `pdf_base64`, y el backend lo adjunta al correo.
    const dataUrl = doc.output("dataurlstring");
    const idx = dataUrl.indexOf(",");
    return idx > 0 ? dataUrl.slice(idx + 1) : dataUrl;
  } else {
    return URL.createObjectURL(pdfBlob);
  }
};